/**
 * Modern Bold · Product Detail Page — small custom elements
 * ─────────────────────────────────────────────────────────────────────────
 * The big two PDP custom elements (gallery + variant picker) live in their
 * own files so they can load only where they're needed:
 *   modern-product-gallery.js  — <mb-product-gallery>
 *   modern-product-picker.js   — <mb-variant-picker>
 *
 * This file holds the small, low-risk PDP-adjacent elements:
 *   <mb-recently-viewed-tracker> mount-time push to MBRecentlyViewed
 *   <modern-product-recommendations> SSR fallback fetcher (legacy element)
 *
 * <mb-wishlist-button> lives in modern-wishlist.js (shared across PDP and
 * collection/search surfaces — each section loads it independently).
 *
 * Browser drives lifecycle via connectedCallback / disconnectedCallback.
 * No IIFE, no global init, no shopify:section:load wiring needed.
 */

// ───────────────────────────────────────────────────────────────────────────
// <mb-recently-viewed-tracker>
// Wraps a <script type="application/json" data-mb-product-snapshot> child
// and pushes its payload into the MBRecentlyViewed store on connect. No
// teardown — write-once semantics; nothing to clean up.
// ───────────────────────────────────────────────────────────────────────────

if (!customElements.get('mb-recently-viewed-tracker')) {
  class MBRecentlyViewedTracker extends HTMLElement {
    connectedCallback() {
      if (!window.MBRecentlyViewed) return;
      const node = this.querySelector('[data-mb-product-snapshot]');
      if (!node) return;
      try {
        const snapshot = JSON.parse(node.textContent);
        if (snapshot && snapshot.id) window.MBRecentlyViewed.add(snapshot);
      } catch (_) { /* malformed JSON — non-critical, skip */ }
    }
  }
  customElements.define('mb-recently-viewed-tracker', MBRecentlyViewedTracker);
}

// ───────────────────────────────────────────────────────────────────────────
// <modern-product-recommendations>
// Pre-existing element, unchanged. Lazily fetches the recommendations
// fragment if the SSR slot is empty.
// ───────────────────────────────────────────────────────────────────────────

if (!customElements.get('modern-product-recommendations')) {
  class ModernProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      const grid = this.querySelector('.mb-pdp-related__grid');
      if (grid && grid.children.length > 0) return;
      this._fetch(url);
    }
    _fetch(url) {
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('modern-product-recommendations');
          if (inner && inner.innerHTML.trim()) this.innerHTML = inner.innerHTML;
        })
        .catch(() => { /* SSR fallback already on-page */ });
    }
  }
  customElements.define('modern-product-recommendations', ModernProductRecommendations);
}
